/**
 * Núcleo puro de crafting/profissões (PLANO-DIFERENCIAIS Pilar 1 — Economia de Produção).
 * Sem DOM, sem I/O. Opera sobre um `state` genérico: `state.professions`, `state.drops`,
 * `state.inventory`, `state.stats.coins`.
 */

import { FOSSIL_REVIVALS, PROFESSION_KEYS, RECIPES_BY_ID } from './recipes.mjs';

export { PROFESSION_KEYS, FOSSIL_REVIVALS, RECIPES_BY_ID, recipesForProfession, RECIPES } from './recipes.mjs';

export const MAX_PROFESSION_LEVEL = 30;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/** Curva de XP por profissão: sobe suave no começo, mais cara perto do teto. */
export function professionXpToNext(level) {
  const value = clamp(Math.floor(Number(level) || 0), 0, MAX_PROFESSION_LEVEL - 1);
  return Math.round(50 + value * 35 + value * value * 4);
}

export function normalizeProfessions(state) {
  if (!state || typeof state !== 'object') return state;
  const current = state.professions && typeof state.professions === 'object' ? state.professions : {};
  state.professions = {};
  for (const key of PROFESSION_KEYS) {
    const entry = current[key] && typeof current[key] === 'object' ? current[key] : {};
    state.professions[key] = {
      level: clamp(Math.floor(Number(entry.level) || 1), 1, MAX_PROFESSION_LEVEL),
      xp: Math.max(0, Math.floor(Number(entry.xp) || 0)),
    };
  }
  return state;
}

function coinsOf(state) {
  if (state?.stats && typeof state.stats.coins === 'number') return state.stats.coins;
  return Number(state?.coins) || 0;
}

function setCoins(state, value) {
  if (state?.stats && typeof state.stats === 'object') state.stats.coins = value;
  else state.coins = value;
}

function bagCount(state, slug) {
  return (Number(state?.drops?.[slug]) || 0) + (Number(state?.inventory?.[slug]) || 0);
}

/** Consome primeiro de `state.drops` (loot bruto), depois de `state.inventory`. */
function consumeItem(state, slug, qty) {
  let left = qty;
  for (const bucket of [state.drops, state.inventory]) {
    if (!bucket || left <= 0) continue;
    const take = Math.min(left, Number(bucket[slug]) || 0);
    if (take <= 0) continue;
    bucket[slug] -= take;
    if (bucket[slug] <= 0) delete bucket[slug];
    left -= take;
  }
  return left <= 0;
}

function grantItem(state, slug, qty) {
  if (!state.inventory || typeof state.inventory !== 'object') state.inventory = {};
  state.inventory[slug] = (Number(state.inventory[slug]) || 0) + qty;
}

/** XP de profissão com level-up em cadeia (o excedente rola para o próximo nível). */
export function grantProfessionXp(state, professionKey, amount) {
  normalizeProfessions(state);
  const entry = state.professions[professionKey];
  if (!entry || !Number.isFinite(amount) || amount <= 0) return { leveledUp: false, level: entry?.level || 1 };
  entry.xp += Math.floor(amount);
  let leveledUp = false;
  while (entry.level < MAX_PROFESSION_LEVEL && entry.xp >= professionXpToNext(entry.level)) {
    entry.xp -= professionXpToNext(entry.level);
    entry.level += 1;
    leveledUp = true;
  }
  return { leveledUp, level: entry.level };
}

export function canCraft(state, recipeId, qty = 1) {
  const recipe = RECIPES_BY_ID[recipeId];
  if (!recipe) return false;
  const times = Math.max(1, Math.floor(Number(qty) || 1));
  const level = state?.professions?.[recipe.profession]?.level || 1;
  if (level < recipe.level) return false;
  if ((recipe.coinsCost || 0) * times > coinsOf(state)) return false;
  return Object.entries(recipe.ingredients).every(([slug, need]) => bagCount(state, slug) >= need * times);
}

/**
 * Crafta `qty` unidades da receita: consome ingredientes + IdleCoin, credita o produto
 * em `state.inventory` e concede XP de profissão. Retorno estável: `{ ok, error, produced, xpGained, leveledUp }`.
 */
export function craftItem(state, recipeId, qty = 1) {
  const recipe = RECIPES_BY_ID[recipeId];
  if (!state || typeof state !== 'object' || !recipe) return { ok: false, error: 'invalid_recipe' };
  normalizeProfessions(state);
  const times = Math.max(1, Math.floor(Number(qty) || 1));
  const level = state.professions[recipe.profession].level;
  if (level < recipe.level) return { ok: false, error: 'profession_level_too_low' };
  const coinsNeeded = (recipe.coinsCost || 0) * times;
  if (coinsNeeded > coinsOf(state)) return { ok: false, error: 'insufficient_funds' };
  const missing = Object.entries(recipe.ingredients).find(([slug, need]) => bagCount(state, slug) < need * times);
  if (missing) return { ok: false, error: 'missing_ingredients', ingredient: missing[0] };

  for (const [slug, need] of Object.entries(recipe.ingredients)) consumeItem(state, slug, need * times);
  if (coinsNeeded) setCoins(state, coinsOf(state) - coinsNeeded);
  const producedQty = recipe.product.qty * times;
  grantItem(state, recipe.product.slug, producedQty);
  const { leveledUp, level: newLevel } = grantProfessionXp(state, recipe.profession, recipe.xp * times);

  return {
    ok: true,
    produced: { slug: recipe.product.slug, qty: producedQty },
    xpGained: recipe.xp * times,
    leveledUp,
    level: newLevel,
  };
}

/** Revive um fóssil real (Dome/Helix/Old Amber) em espécie, custando só IdleCoin. */
export function reviveFossil(state, fossilSlug) {
  const plan = FOSSIL_REVIVALS[fossilSlug];
  if (!state || typeof state !== 'object' || !plan) return { ok: false, error: 'invalid_fossil' };
  if (bagCount(state, fossilSlug) < 1) return { ok: false, error: 'missing_fossil' };
  if (coinsOf(state) < plan.coinsCost) return { ok: false, error: 'insufficient_funds' };
  consumeItem(state, fossilSlug, 1);
  setCoins(state, coinsOf(state) - plan.coinsCost);
  return { ok: true, species: plan.species, coinsCost: plan.coinsCost };
}
